import React, { useEffect, useMemo, type ComponentPropsWithRef } from "react";
import { useField } from "formik";
import { RuleConditionAmount } from "@common/types/transactionRule";
import { Input } from "@design-system/Input/Input";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    AmountConditionType,
    AmountRoundingConditionType,
    TransactionRuleConditionFormSchema,
} from "../constants";
import styles from "../RuleForm.module.scss";

const AmountInput = (props: Omit<ComponentPropsWithRef<"input">, "size">) => (
    <Input {...props} size="lg" className={styles.amountInput} />
);

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
    allowedAmountIsCredit?: boolean;
    amountDirectionTagClassName?: string;
}

const conditionTypeItems = Object.values(AmountConditionType).map((value) => ({
    value,
    label: value,
}));

interface AmountDirectionOption {
    value: "credit" | "debit";
    label: string;
}

const isCreditItems: AmountDirectionOption[] = [
    { value: "credit", label: "Cash in" },
    { value: "debit", label: "Cash out" },
];

const roundingLevelItems = Object.values(AmountRoundingConditionType).map(
    (value) => ({
        value,
        label: value,
    }),
);

export const AmountConditionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
    allowedAmountIsCredit,
    amountDirectionTagClassName,
}) => {
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["amountConditionType"]
    >(`conditions.${index}.amountConditionType`);
    const [isCreditField, , isCreditHelpers] = useField<
        TransactionRuleConditionFormSchema["amountIsCredit"]
    >(`conditions.${index}.amountIsCredit`);
    const [singleValueField, , singleValueHelpers] = useField<
        TransactionRuleConditionFormSchema["amountSingleValue"]
    >(`conditions.${index}.amountSingleValue`);
    const [valueRangeField, , valueRangeHelpers] = useField<
        TransactionRuleConditionFormSchema["amountValueRange"]
    >(`conditions.${index}.amountValueRange`);
    const [roundingLevelField, , roundingLevelHelpers] = useField<
        TransactionRuleConditionFormSchema["amountRoundingLevel"]
    >(`conditions.${index}.amountRoundingLevel`);
    const { setValue: setAmountIsCredit } = isCreditHelpers;

    useEffect(() => {
        if (
            allowedAmountIsCredit === undefined ||
            isCreditField.value === allowedAmountIsCredit
        ) {
            return;
        }
        setAmountIsCredit(allowedAmountIsCredit);
    }, [allowedAmountIsCredit, isCreditField.value, setAmountIsCredit]);

    const isCreditDropdownValue = useMemo(() => {
        if (isCreditField.value !== undefined) {
            return isCreditField.value ? "credit" : "debit";
        }

        return null;
    }, [isCreditField.value]);

    const setRangeValue = (
        value: number | undefined,
        key: keyof RuleConditionAmount,
    ) => {
        valueRangeHelpers.setValue({
            ...(valueRangeField.value ?? {}),
            [key]: value,
        });
    };

    const controlTrigger = (
        <Picker.Button variant="primary" size={controlButtonSize ?? "md"} />
    );

    return (
        <>
            {allowedAmountIsCredit === undefined ? (
                <Picker.Select
                    items={isCreditItems}
                    value={isCreditDropdownValue}
                    onValueChange={(v) => {
                        if (v !== null) {
                            setAmountIsCredit(v === "credit");
                        }
                    }}
                    placeholder="Select cash in or out"
                    trigger={controlTrigger}
                />
            ) : (
                <span className={amountDirectionTagClassName}>
                    {allowedAmountIsCredit ? "Cash in" : "Cash out"}
                </span>
            )}

            <Picker.Select
                items={conditionTypeItems}
                value={conditionTypeField.value ?? null}
                onValueChange={(v) => {
                    if (v !== null) {
                        conditionTypeHelpers.setValue(v);
                    }
                }}
                placeholder="Select type"
                trigger={controlTrigger}
            />

            {[
                AmountConditionType.GREATER_THAN,
                AmountConditionType.LESS_THAN,
                AmountConditionType.IS,
            ].includes(conditionTypeField.value) && (
                <NumberFormat
                    value={singleValueField.value}
                    onValueChange={(v) =>
                        singleValueHelpers.setValue(v.floatValue)
                    }
                    prefix="$ "
                    decimalScale={2}
                    allowEmptyFormatting
                    thousandSeparator
                    allowNegative={false}
                    customInput={AmountInput}
                />
            )}
            {conditionTypeField.value === AmountConditionType.BETWEEN && (
                <>
                    <NumberFormat
                        value={valueRangeField.value?.min}
                        onValueChange={(v) =>
                            setRangeValue(v.floatValue, "min")
                        }
                        prefix="$ "
                        decimalScale={2}
                        allowEmptyFormatting
                        thousandSeparator
                        allowNegative={false}
                        customInput={AmountInput}
                    />
                    and
                    <NumberFormat
                        value={valueRangeField.value?.max}
                        onValueChange={(v) =>
                            setRangeValue(v.floatValue, "max")
                        }
                        prefix="$ "
                        decimalScale={2}
                        allowEmptyFormatting
                        thousandSeparator
                        allowNegative={false}
                        customInput={AmountInput}
                    />
                </>
            )}
            {conditionTypeField.value === AmountConditionType.IS_ROUND && (
                <Picker.Select
                    items={roundingLevelItems}
                    value={roundingLevelField.value ?? null}
                    onValueChange={(v) => {
                        if (v !== null) {
                            roundingLevelHelpers.setValue(v);
                        }
                    }}
                    placeholder="Select rounding level"
                    trigger={controlTrigger}
                />
            )}
        </>
    );
};
